import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import sunTexture from '../img/sun.jpg';
import mercuryTexture from '../img/mercury.jpg';
import venusTexture from '../img/venus.jpg';
import earthTextureFile from '../img/earth.jpg';
import marsTexture from '../img/mars.jpg';
import jupiterTexture from '../img/jupiter.jpg';
import saturnTexture from '../img/saturn.jpg';
import saturnRingTexture from '../img/saturn ring.png';
import uranusTexture from '../img/uranus.jpg';
import uranusRingTexture from '../img/uranus ring.png';
import neptuneTexture from '../img/neptune.jpg';
import plutoTexture from '../img/pluto.jpg';
import logoUrl from '../img/Logo.png';

// --- LOADING MANAGER ---
const loadingManager = new THREE.LoadingManager();
loadingManager.onLoad = () => {
  const loadingScreen = document.getElementById('loading-screen');
  if (loadingScreen) loadingScreen.style.display = 'none';
};

const textureLoader = new THREE.TextureLoader(loadingManager);

// --- RENDERER ---
const renderer = new THREE.WebGLRenderer();
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setClearColor('#111111');
document.body.appendChild(renderer.domElement);

// --- SCENE ---
const scene = new THREE.Scene();

// --- CAMERA & CONTROLS ---
const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
camera.position.set(-90, 140, 140);
const orbit = new OrbitControls(camera, renderer.domElement);
orbit.update();

// --- LIGHT ---
const ambientLight = new THREE.AmbientLight('#333333');
scene.add(ambientLight);
const pointLight = new THREE.PointLight('#ffffff', 2, 300);
scene.add(pointLight);

// --- SUN ---
const sunGeo = new THREE.SphereGeometry(16, 30, 30);
const sunMat = new THREE.MeshBasicMaterial({ map: textureLoader.load(sunTexture) });
const sun = new THREE.Mesh(sunGeo, sunMat);
scene.add(sun);

// --- PLANET CREATOR ---
const createPlanet = (size, texture, position, ring) => {
  const geo = new THREE.SphereGeometry(size, 30, 30);
  const mat = new THREE.MeshStandardMaterial({ map: textureLoader.load(texture) });
  const mesh = new THREE.Mesh(geo, mat);
  const obj = new THREE.Object3D();
  obj.add(mesh);

  if (ring) {
    const ringGeo = new THREE.RingGeometry(ring.innerRadius, ring.outerRadius, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      map: textureLoader.load(ring.texture),
      transparent: true,
      opacity: 0.9,
      side: THREE.DoubleSide
    });
    const ringMesh = new THREE.Mesh(ringGeo, ringMat);
    ringMesh.position.x = position;
    ringMesh.rotation.x = -0.5 * Math.PI;
    obj.add(ringMesh);
  }

  mesh.position.x = position;
  scene.add(obj);
  return { mesh, obj };
};

// --- PLANETS ---
const mercury = createPlanet(3.2, mercuryTexture, 28);
const venus = createPlanet(5.8, venusTexture, 44);
const earth = createPlanet(6, earthTextureFile, 62);
const mars = createPlanet(6, marsTexture, 78);
const jupiter = createPlanet(12, jupiterTexture, 100);
const saturn = createPlanet(10, saturnTexture, 132, {
  innerRadius: 10,
  outerRadius: 20,
  texture: saturnRingTexture
});
const uranus = createPlanet(7, uranusTexture, 176, {
  innerRadius: 7,
  outerRadius: 12,
  texture: uranusRingTexture
});
const neptune = createPlanet(7, neptuneTexture, 200);
const pluto = createPlanet(2.8, plutoTexture, 216);

// --- ANIMATE ---
function animate() {
  sun.rotateY(0.002);
  mercury.mesh.rotateY(0.002); mercury.obj.rotateY(0.002);
  venus.mesh.rotateY(0.001);   venus.obj.rotateY(0.0075);
  earth.mesh.rotateY(0.01);    earth.obj.rotateY(0.005);
  mars.mesh.rotateY(0.009);    mars.obj.rotateY(0.004);
  jupiter.mesh.rotateY(0.002); jupiter.obj.rotateY(0.001);
  saturn.mesh.rotateY(0.019);  saturn.obj.rotateY(0.00045);
  uranus.mesh.rotateY(0.015);  uranus.obj.rotateY(0.0002);
  neptune.mesh.rotateY(0.016); neptune.obj.rotateY(0.00005);
  pluto.mesh.rotateY(0.004);   pluto.obj.rotateY(0.000035);

  renderer.render(scene, camera);
}

renderer.setAnimationLoop(animate);

// --- RESPONSIVE ---
window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  createStars(); // refresh stelle
});

// --- FOOTER ---
const footer = document.createElement('footer');
footer.className = 'footer';
footer.innerHTML = `
  <p>© ${new Date().getFullYear()} Solar System, Powered by 
    <a href="https://ansoweb.com" target="_blank" rel="noopener noreferrer">Ansoweb</a>. All rights reserved.
  </p>`;
document.body.appendChild(footer);

// --- STARS BACKGROUND ---
const numStars = 200;
function createStars() {
  document.querySelectorAll('.star').forEach(star => star.remove());
  for (let i = 0; i < numStars; i++) {
    const star = document.createElement('div');
    star.className = 'star';
    const [x, y] = getRandomPosition();
    star.style.top = `${x}px`;
    star.style.left = `${y}px`;
    document.body.appendChild(star);
  }
}
function getRandomPosition() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  return [
    Math.floor(Math.random() * height),
    Math.floor(Math.random() * width)
  ];
}
createStars();

// --- HEADER ---
const header = document.createElement('header');
header.className = 'site-header';
header.innerHTML = `<img src="${logoUrl}" alt="Logo Solar System" class="site-logo">`;
document.body.prepend(header);
